const SSO_ROUTE = "/sso";
export const SSO_LOGIN_ERROR_QUERY = "sso_error";
export const SSO_HANDOFF_TOKEN_KEY = "sso_handoff_token";
export const SSO_HANDOFF_RETRIES_KEY = "sso_handoff_retries";
export const SSO_HANDOFF_STARTED_KEY = "sso_handoff_started_at";
export const SSO_HANDOFF_LOCK_KEY = "sso_handoff_lock";
export const SSO_HANDOFF_LOCK_MS = 45 * 1000;
export const SSO_MAX_HANDOFF_RETRIES = 8;
export const SSO_MAX_API_ATTEMPTS = 8;
export const SSO_HANDOFF_TTL_MS = 2 * 60 * 1000;

const SSO_TOKEN_EXCLUDED_PREFIXES = [
  "/bizassure/link/callback",
  "/password-reset",
  "/agency/signup",
  "/agent/signup",
];

export const isSsoRoute = (path = "") => {
  const normalized = String(path || "");
  return normalized === SSO_ROUTE || normalized.startsWith(`${SSO_ROUTE}/`);
};

export const isExcludedSsoTokenPath = (path = "") => {
  const normalized = String(path || "");
  return SSO_TOKEN_EXCLUDED_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
};

export const hasSsoTokenQuery = (search = "") => {
  try {
    const params = new URLSearchParams(
      String(search || "").startsWith("?") ? search.slice(1) : search
    );
    const token = params.get("token");
    return !!(token && String(token).trim());
  } catch (error) {
    return false;
  }
};

export const shouldRedirectSsoTokenToRoute = (path = "", search = "") => {
  if (!hasSsoTokenQuery(search)) {
    return false;
  }

  if (isSsoRoute(path) || isExcludedSsoTokenPath(path)) {
    return false;
  }

  return true;
};

export const buildSsoEntryUrl = (search = "") => {
  try {
    const params = new URLSearchParams(
      String(search || "").startsWith("?") ? search.slice(1) : search
    );
    const token = params.get("token");
    if (!token || !String(token).trim()) {
      return null;
    }

    return `${SSO_ROUTE}?token=${encodeURIComponent(String(token).trim())}`;
  } catch (error) {
    return null;
  }
};

/**
 * BizAssure portal opens Protego with ?token= on the configured base URL.
 * If that URL is not /sso, main.js would bootstrap a stale cookie session and
 * auth middleware would send the user to /login without ever mounting Sso.vue.
 */
export const redirectSsoTokenEntryIfNeeded = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const { pathname, search } = window.location;
  if (!shouldRedirectSsoTokenToRoute(pathname, search)) {
    return false;
  }

  const target = buildSsoEntryUrl(search);
  if (!target) {
    return false;
  }

  window.location.replace(target);
  return true;
};

export const formatSsoFailureMessage = (error) => {
  const apiMessage =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message;

  return apiMessage ? String(apiMessage).trim() : "Unknown error";
};

export const toUserFacingSsoError = (message = "") => {
  const normalized = String(message || "").trim();

  if (!normalized) {
    return "BizAssure sign-in failed. Please try again from BizAssure.";
  }

  if (/no token|no sso token/i.test(normalized)) {
    return "No sign-in token was provided. Please open Protego from BizAssure again.";
  }

  if (/expired/i.test(normalized)) {
    return "Your BizAssure sign-in link has expired. Please open Protego again from BizAssure.";
  }

  if (/already been used/i.test(normalized)) {
    return "This BizAssure sign-in link was already used. Please open Protego again from BizAssure.";
  }

  if (/not available for bizassure admin/i.test(normalized)) {
    return "BizAssure admin accounts cannot sign in to Protego with SSO.";
  }

  if (/session establishment failed|refresh session/i.test(normalized)) {
    return "BizAssure sign-in succeeded but Protego could not start your session. Please try again.";
  }

  if (/authentication failed/i.test(normalized)) {
    return normalized.replace(/^Authentication failed:\s*/i, "BizAssure sign-in failed: ");
  }

  return `BizAssure sign-in failed: ${normalized}`;
};

export const readSsoErrorFromQuery = (query = {}) => {
  const raw = query?.[SSO_LOGIN_ERROR_QUERY];
  if (!raw) {
    return "";
  }

  const value = Array.isArray(raw) ? raw[0] : raw;
  return String(value);
};

export const getSsoRetryDelayMs = (attempt = 1) => Math.min(400 * attempt, 2400);

export const isRetryableSsoError = (error) => {
  const message = formatSsoFailureMessage(error).toLowerCase();

  if (/expired/.test(message)) {
    return false;
  }

  if (/not available for bizassure admin/.test(message)) {
    return false;
  }

  if (/no token|no sso token/.test(message)) {
    return false;
  }

  if (/already been used/.test(message)) {
    return true;
  }

  const status = error?.response?.status;
  if (!status) {
    return true;
  }

  return status === 401 || status === 403 || status === 408 || status === 429 || status >= 500;
};

export const isSsoHandoffActive = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    isSsoRoute(window.location.pathname || "")
    || hasSsoTokenQuery(window.location.search || "")
  );
};

export const isSsoHandoffLocked = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const lockedAt = parseInt(window.sessionStorage.getItem(SSO_HANDOFF_LOCK_KEY) || "0", 10);
  return !!lockedAt && !Number.isNaN(lockedAt) && Date.now() - lockedAt < SSO_HANDOFF_LOCK_MS;
};

export const markSsoHandoffLocked = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(SSO_HANDOFF_LOCK_KEY, String(Date.now()));
};

export const clearSsoHandoffLock = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SSO_HANDOFF_LOCK_KEY);
};

export const beginSsoHandoff = (token) => {
  if (typeof window === "undefined" || !token) {
    return;
  }

  window.sessionStorage.setItem(SSO_HANDOFF_TOKEN_KEY, String(token).trim());
  window.sessionStorage.setItem(SSO_HANDOFF_RETRIES_KEY, "0");
  window.sessionStorage.setItem(SSO_HANDOFF_STARTED_KEY, String(Date.now()));
  markSsoHandoffLocked();
};

export const clearSsoHandoff = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SSO_HANDOFF_TOKEN_KEY);
  window.sessionStorage.removeItem(SSO_HANDOFF_RETRIES_KEY);
  window.sessionStorage.removeItem(SSO_HANDOFF_STARTED_KEY);
  clearSsoHandoffLock();
};

export const getSsoHandoffState = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const token = window.sessionStorage.getItem(SSO_HANDOFF_TOKEN_KEY);
  const retries = parseInt(window.sessionStorage.getItem(SSO_HANDOFF_RETRIES_KEY) || "0", 10);
  const startedAt = parseInt(window.sessionStorage.getItem(SSO_HANDOFF_STARTED_KEY) || "0", 10);

  if (!token) {
    return null;
  }

  return {
    token,
    retries: Number.isNaN(retries) ? 0 : retries,
    startedAt: Number.isNaN(startedAt) ? 0 : startedAt,
  };
};

export const shouldAutoRetrySsoFromLogin = () => {
  const state = getSsoHandoffState();
  if (!state) {
    return false;
  }

  if (!state.startedAt || Date.now() - state.startedAt > SSO_HANDOFF_TTL_MS) {
    clearSsoHandoff();
    return false;
  }

  return state.retries < SSO_MAX_HANDOFF_RETRIES;
};

export const recordSsoHandoffRetry = () => {
  const state = getSsoHandoffState();
  if (!state) {
    return null;
  }

  const nextRetries = state.retries + 1;
  window.sessionStorage.setItem(SSO_HANDOFF_RETRIES_KEY, String(nextRetries));

  return {
    ...state,
    retries: nextRetries,
  };
};
